/**
 * Response Normalization Service
 * Converts all response formats to a standardized structure for querying and analysis
 */

import { detectQuestionVersion, getCurrentScoringVersion } from '@/data/questionRegistry';

export interface NormalizedResponse {
  questionKey: string;      // Standardized key
  questionIndex: number;    // Position in assessment (0-based)
  value: number;            // User's response (1-7 scale)
  domain?: string;          // TPS domain (e.g., 'external')
  triad?: string;           // TPS triad (e.g., 'control')
  position?: string;        // TPS position (e.g., 'structured')
  timestamp?: string;       // When answered (if tracked)
}

export interface NormalizedResponseSet {
  responses: NormalizedResponse[];
  responseMap: Record<string, number>;  // Quick lookup: key -> value
  metadata: {
    testType: string;
    testVersion: string;
    questionsVersion: string;
    scoringVersion: string;
    totalQuestions: number;
    answeredQuestions: number;
    completionPercentage: number;
    format: 'v2_array' | 'v3_keyed' | 'integral';
  };
}

/**
 * Parse a v3 keyed question ID into its components
 * Format: domain_triad_position_index (e.g., "external_control_structured_0")
 */
function parseV3Key(key: string): { domain?: string; triad?: string; position?: string; index: number } {
  const parts = key.split('_');
  if (parts.length >= 4) {
    return {
      domain: parts[0],
      triad: parts[1],
      position: parts[2],
      index: parseInt(parts[3], 10) || 0,
    };
  }
  return { index: 0 };
}

/**
 * Convert v2 array responses to normalized format
 * Array format: [4, 3, 5, 2, ...] where index = question number
 */
export function normalizeV2Responses(responses: number[]): NormalizedResponseSet {
  const normalized: NormalizedResponse[] = responses.map((value, index) => ({
    questionKey: `legacy_q_${index}`,
    questionIndex: index,
    value,
  }));

  const responseMap: Record<string, number> = {};
  normalized.forEach(r => {
    responseMap[r.questionKey] = r.value;
  });

  return {
    responses: normalized,
    responseMap,
    metadata: {
      testType: 'tps',
      testVersion: 'v2.0.0',
      questionsVersion: '2.0.0',
      scoringVersion: getCurrentScoringVersion('tps'),
      totalQuestions: 108,
      answeredQuestions: responses.filter(v => v !== undefined && v !== null).length,
      completionPercentage: (responses.filter(v => v !== undefined && v !== null).length / 108) * 100,
      format: 'v2_array',
    },
  };
}

/**
 * Convert v3 keyed responses to normalized format
 * Keyed format: { "external_control_structured_0": 4, ... }
 */
export function normalizeV3Responses(responses: Record<string, number>): NormalizedResponseSet {
  const entries = Object.entries(responses);
  
  const normalized: NormalizedResponse[] = entries.map(([key, value], idx) => {
    const parsed = parseV3Key(key);
    return {
      questionKey: key,
      questionIndex: idx,
      value,
      domain: parsed.domain,
      triad: parsed.triad,
      position: parsed.position,
    };
  });

  const responseMap: Record<string, number> = { ...responses };

  return {
    responses: normalized,
    responseMap,
    metadata: {
      testType: 'tps',
      testVersion: 'v3.0.0',
      questionsVersion: '3.0.0',
      scoringVersion: getCurrentScoringVersion('tps'),
      totalQuestions: 108,
      answeredQuestions: entries.length,
      completionPercentage: (entries.length / 108) * 100,
      format: 'v3_keyed',
    },
  };
}

/**
 * Normalize Integral assessment responses
 * Format: { chatHistory: [...], quizAnswers: [...] }
 */
export function normalizeIntegralResponses(
  responses: { chatHistory?: any[]; quizAnswers?: any[] }
): NormalizedResponseSet {
  const normalized: NormalizedResponse[] = [];
  const responseMap: Record<string, number> = {};

  // Extract quiz answers if present
  if (responses.quizAnswers && Array.isArray(responses.quizAnswers)) {
    responses.quizAnswers.forEach((answer, index) => {
      const key = `integral_quiz_${index}`;
      const value = typeof answer === 'number' ? answer : 0;
      normalized.push({
        questionKey: key,
        questionIndex: index,
        value,
      });
      responseMap[key] = value;
    });
  }

  // Count chat exchanges
  const chatCount = responses.chatHistory?.length || 0;

  return {
    responses: normalized,
    responseMap,
    metadata: {
      testType: 'integral',
      testVersion: 'v1.0.0',
      questionsVersion: '1.0.0',
      scoringVersion: getCurrentScoringVersion('integral'),
      totalQuestions: chatCount,
      answeredQuestions: normalized.length,
      completionPercentage: chatCount > 0 ? 100 : 0,
      format: 'integral',
    },
  };
}

/**
 * Auto-detect and normalize any response format
 */
export function normalizeResponses(
  responses: number[] | Record<string, number> | { chatHistory?: any[]; quizAnswers?: any[] } | null,
  testType: string = 'tps'
): NormalizedResponseSet | null {
  if (!responses) return null;

  // Check for Integral format
  if (testType === 'integral' || (typeof responses === 'object' && 'chatHistory' in responses)) {
    return normalizeIntegralResponses(responses as { chatHistory?: any[]; quizAnswers?: any[] });
  }
  
  // Array = v2 format
  if (Array.isArray(responses)) {
    return normalizeV2Responses(responses);
  }
  
  // Object with underscore keys = v3 format
  if (typeof responses === 'object') {
    return normalizeV3Responses(responses as Record<string, number>);
  }
  
  return null;
}

/**
 * Convert normalized responses back to v3 keyed format
 * Useful for re-scoring with current algorithm
 */
export function toV3Format(normalized: NormalizedResponseSet): Record<string, number> {
  if (normalized.metadata.format === 'v3_keyed') {
    return normalized.responseMap;
  }
  
  // For v2 format, we can't perfectly reconstruct v3 keys without question mapping
  // Return best effort with legacy keys
  return normalized.responseMap;
}

/**
 * Get responses grouped by domain (for TPS)
 */
export function getResponsesByDomain(
  normalized: NormalizedResponseSet
): Record<string, NormalizedResponse[]> {
  const byDomain: Record<string, NormalizedResponse[]> = {};
  
  for (const response of normalized.responses) {
    const domain = response.domain || 'unknown';
    if (!byDomain[domain]) {
      byDomain[domain] = [];
    }
    byDomain[domain].push(response);
  }
  
  return byDomain;
}

/**
 * Calculate average response value (useful for analysis)
 */
export function calculateAverageResponse(normalized: NormalizedResponseSet): number {
  if (normalized.responses.length === 0) return 0;
  const sum = normalized.responses.reduce((acc, r) => acc + r.value, 0);
  return +(sum / normalized.responses.length).toFixed(2);
}
